import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { InvoiceManagement } from './invoiceManagementService';
import { addTransaction } from './financeService';

export interface AccountReceivable {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  date: Date | Timestamp;
  clientId: string;
  clientName: string;
  clientType: 'Individual' | 'Factory' | 'Bank';
  clientEmail?: string;
  serviceDescription: string;
  totalGuards: number;
  guardDeploymentAmount: number;
  totalAmount: number;
  status: 'outstanding' | 'partial' | 'paid';
  dueDate?: Date | Timestamp;
  paidDate?: Date | Timestamp;
  paidAmount: number;
  remainingAmount: number;
  paymentMethod?: string;
  notes?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

/**
 * Get all account receivables
 */
export const fetchAccountReceivables = async (companyId: string): Promise<AccountReceivable[]> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const receivablesRef = collection(financeDocRef, 'accountReceivables');
  const q = query(receivablesRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : data.dueDate ? new Date(data.dueDate) : undefined,
      paidDate: data.paidDate?.toDate ? data.paidDate.toDate() : data.paidDate ? new Date(data.paidDate) : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as AccountReceivable;
  });
};

/**
 * Create account receivable from a regular invoice (from InvoiceRevenuePage)
 */
export const createAccountReceivableFromInvoice = async (
  companyId: string,
  invoice: {
    id: string;
    invoiceNumber: string;
    date: Date | Timestamp | any;
    amount: number;
    clientName: string;
    clientType?: 'Individual' | 'Factory' | 'Bank';
    description: string;
    dueDate?: Date | Timestamp | any;
    status?: string;
  }
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const receivablesRef = collection(financeDocRef, 'accountReceivables');
  
  // Check if account receivable already exists for this invoice number
  const existingQuery = query(receivablesRef, where('invoiceNumber', '==', invoice.invoiceNumber));
  const existingSnapshot = await getDocs(existingQuery);
  
  if (!existingSnapshot.empty) {
    // Account receivable already exists, return existing ID
    return existingSnapshot.docs[0].id;
  }
  
  const receivableRef = doc(receivablesRef);
  
  let invoiceDate: Date;
  if (invoice.date instanceof Date) {
    invoiceDate = invoice.date;
  } else if (invoice.date && typeof (invoice.date as any).toDate === 'function') {
    invoiceDate = (invoice.date as any).toDate();
  } else if (invoice.date && typeof invoice.date === 'object' && (invoice.date as any).seconds) {
    invoiceDate = new Date((invoice.date as any).seconds * 1000);
  } else {
    invoiceDate = new Date(invoice.date as any);
  }
  
  let dueDateObj: Date | undefined;
  if (invoice.dueDate) {
    if (invoice.dueDate instanceof Date) {
      dueDateObj = invoice.dueDate;
    } else if (typeof (invoice.dueDate as any).toDate === 'function') {
      dueDateObj = (invoice.dueDate as any).toDate();
    } else if (typeof invoice.dueDate === 'object' && (invoice.dueDate as any).seconds) {
      dueDateObj = new Date((invoice.dueDate as any).seconds * 1000);
    } else {
      dueDateObj = new Date(invoice.dueDate as any);
    }
  }

  // Determine status based on invoice status
  let receivableStatus: 'outstanding' | 'partial' | 'paid';
  if (invoice.status === 'received') {
    receivableStatus = 'paid';
  } else {
    receivableStatus = 'outstanding';
  }

  const receivable: any = {
    companyId,
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    clientId: '', // Not available from regular invoice
    clientName: invoice.clientName,
    clientType: invoice.clientType || 'Individual',
    clientEmail: null,
    serviceDescription: invoice.description,
    totalGuards: 0, // Not available from regular invoice
    guardDeploymentAmount: 0, // Not available from regular invoice
    totalAmount: invoice.amount,
    status: receivableStatus,
    paidAmount: invoice.status === 'received' ? invoice.amount : 0,
    remainingAmount: invoice.status === 'received' ? 0 : invoice.amount,
    notes: null,
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(invoiceDate),
  };

  if (dueDateObj) {
    receivable.dueDate = Timestamp.fromDate(dueDateObj);
  }

  if (invoice.status === 'received') {
    receivable.paidDate = serverTimestamp();
  }

  await setDoc(receivableRef, receivable);
  return receivableRef.id;
};

/**
 * Create account receivable from submitted invoice (InvoiceManagement)
 */
export const createAccountReceivable = async (
  companyId: string,
  invoice: InvoiceManagement
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const receivablesRef = collection(financeDocRef, 'accountReceivables');
  const receivableRef = doc(receivablesRef);
  
  let invoiceDate: Date;
  if (invoice.date instanceof Date) {
    invoiceDate = invoice.date;
  } else if (invoice.date && typeof (invoice.date as any).toDate === 'function') {
    invoiceDate = (invoice.date as any).toDate();
  } else if (invoice.date && typeof invoice.date === 'object' && (invoice.date as any).seconds) {
    invoiceDate = new Date((invoice.date as any).seconds * 1000);
  } else {
    invoiceDate = new Date(invoice.date as any);
  }
  
  let dueDateObj: Date | undefined;
  if (invoice.dueDate) {
    if (invoice.dueDate instanceof Date) {
      dueDateObj = invoice.dueDate;
    } else if (typeof (invoice.dueDate as any).toDate === 'function') {
      dueDateObj = (invoice.dueDate as any).toDate();
    } else if (typeof invoice.dueDate === 'object' && (invoice.dueDate as any).seconds) {
      dueDateObj = new Date((invoice.dueDate as any).seconds * 1000);
    } else {
      dueDateObj = new Date(invoice.dueDate as any);
    }
  }

  const receivable: any = {
    companyId,
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    clientId: invoice.clientId,
    clientName: invoice.clientName,
    clientType: invoice.clientType,
    clientEmail: invoice.clientEmail || null,
    serviceDescription: invoice.serviceDescription,
    totalGuards: invoice.totalGuards,
    guardDeploymentAmount: invoice.guardDeploymentAmount,
    totalAmount: invoice.totalAmount,
    status: 'outstanding',
    paidAmount: 0,
    remainingAmount: invoice.totalAmount,
    notes: invoice.notes || null,
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(invoiceDate),
  };

  if (dueDateObj) {
    receivable.dueDate = Timestamp.fromDate(dueDateObj);
  }

  await setDoc(receivableRef, receivable);
  return receivableRef.id;
};

/**
 * Update account receivable payment
 */
export const updateAccountReceivablePayment = async (
  companyId: string,
  receivableId: string,
  paidAmount: number,
  paymentMethod?: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const receivableRef = doc(financeDocRef, 'accountReceivables', receivableId);
  const receivableDoc = await getDoc(receivableRef);
  
  if (!receivableDoc.exists()) {
    throw new Error('Account receivable not found');
  }

  const data = receivableDoc.data();
  const currentPaidAmount = data.paidAmount || 0;
  const totalAmount = data.totalAmount || 0;
  const newPaidAmount = currentPaidAmount + paidAmount;
  const remainingAmount = totalAmount - newPaidAmount;

  let status: 'outstanding' | 'partial' | 'paid';
  if (remainingAmount <= 0) {
    status = 'paid';
  } else if (newPaidAmount > 0) {
    status = 'partial';
  } else {
    status = 'outstanding';
  }

  const updateData: any = {
    paidAmount: newPaidAmount,
    remainingAmount: remainingAmount,
    status,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  };

  if (paymentMethod) {
    updateData.paymentMethod = paymentMethod;
  }

  if (status === 'paid' && !data.paidDate) {
    updateData.paidDate = serverTimestamp();
  }

  await updateDoc(receivableRef, updateData);

  // Create transaction for the payment received (debit - money in)
  const paymentDate = new Date();
  await addTransaction(companyId, {
    date: paymentDate,
    particulars: `Payment Received - Invoice ${data.invoiceNumber} - ${data.clientName}`,
    ref: data.invoiceNumber,
    debit: paidAmount,
    credit: 0,
    type: 'invoice',
    description: `Payment received for ${data.serviceDescription || 'Account Receivable'}. Payment method: ${paymentMethod || 'Not specified'}`,
  });

  // Create or update invoice in the invoices collection
  const invoicesRef = collection(financeDocRef, 'invoices');
  const invoiceQuery = query(invoicesRef, where('invoiceNumber', '==', data.invoiceNumber));
  const invoiceSnapshot = await getDocs(invoiceQuery);

  let invoiceDate: Date;
  if (data.date instanceof Date) {
    invoiceDate = data.date;
  } else if (data.date && typeof (data.date as any).toDate === 'function') {
    invoiceDate = (data.date as any).toDate();
  } else if (data.date && typeof data.date === 'object' && (data.date as any).seconds) {
    invoiceDate = new Date((data.date as any).seconds * 1000);
  } else {
    invoiceDate = new Date(data.date as any);
  }

  if (invoiceSnapshot.empty) {
    // Create new invoice if it doesn't exist
    const invoiceRef = doc(invoicesRef);
    const invoiceStatus = status === 'paid' ? 'received' : status === 'partial' ? 'pending' : 'pending';
    
    const invoice: any = {
      companyId,
      invoiceNumber: data.invoiceNumber,
      amount: totalAmount,
      paidAmount: paidAmount, // Track paid amount
      clientName: data.clientName,
      clientType: data.clientType,
      description: data.serviceDescription || 'Account Receivable',
      status: invoiceStatus,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      date: Timestamp.fromDate(invoiceDate),
      paymentMethod: paymentMethod || undefined,
    };

    if (status === 'paid') {
      invoice.paymentDate = Timestamp.fromDate(paymentDate);
    }

    if (data.dueDate) {
      let dueDateObj: Date;
      if (data.dueDate instanceof Date) {
        dueDateObj = data.dueDate;
      } else if (data.dueDate && typeof (data.dueDate as any).toDate === 'function') {
        dueDateObj = (data.dueDate as any).toDate();
      } else if (data.dueDate && typeof data.dueDate === 'object' && (data.dueDate as any).seconds) {
        dueDateObj = new Date((data.dueDate as any).seconds * 1000);
      } else {
        dueDateObj = new Date(data.dueDate as any);
      }
      invoice.dueDate = Timestamp.fromDate(dueDateObj);
    }

    await setDoc(invoiceRef, invoice);
  } else {
    // Update existing invoice
    const invoiceDoc = invoiceSnapshot.docs[0];
    const invoiceData = invoiceDoc.data();
    
    // Calculate new paid amount
    const currentPaidAmount = invoiceData.paidAmount || 0;
    const newPaidAmount = currentPaidAmount + paidAmount;
    
    const invoiceUpdateData: any = {
      paidAmount: newPaidAmount,
      updatedAt: serverTimestamp(),
    };

    // Update status based on payment
    if (status === 'paid') {
      invoiceUpdateData.status = 'received';
      invoiceUpdateData.paymentDate = Timestamp.fromDate(paymentDate);
    } else if (status === 'partial' && invoiceData.status !== 'received') {
      invoiceUpdateData.status = 'pending';
    }

    if (paymentMethod) {
      invoiceUpdateData.paymentMethod = paymentMethod;
    }

    await updateDoc(invoiceDoc.ref, invoiceUpdateData);
  }
};

